import type { HelpMap } from './types';

/** Statystyki, wykresy, narzędzia analityczne i eksperymenty. */
export const ANALYSIS: HelpMap = {
  // ——— wskaźniki (KPI) ———
  'kpi.pop': {
    group: 'Analiza i statystyka',
    title: 'Żywe organizmy',
    short: 'Ile organizmów żyje teraz. Wartość zmienia się w rytmie rodzenia i umierania, a w stanie równowagi krąży wokół pojemności środowiska.',
    see: ['carrying', 'chart.pop'],
  },
  'kpi.births': {
    group: 'Analiza i statystyka',
    title: 'Narodziny na 1000 ticków',
    short: 'Średnia liczba urodzin w ostatnich kilkunastu próbkach, przeliczona na 1000 ticków. Wygładzona, bo pojedyncze okna są bardzo szumne.',
    more: ['Gdy narodziny i zgony są sobie równe, populacja stoi w miejscu. Gdy narodziny przewyższają zgony, rośnie.'],
    see: ['kpi.deaths', 'chart.pop'],
  },
  'kpi.deaths': {
    group: 'Analiza i statystyka',
    title: 'Zgony na 1000 ticków',
    short: 'Średnia liczba zgonów z ostatnich próbek na 1000 ticków. Przyczyny rozbija wykres „Przyczyny zgonów”.',
    see: ['death', 'chart.deaths'],
  },
  'kpi.age': {
    group: 'Analiza i statystyka',
    title: 'Średni wiek',
    short: 'Średni wiek żyjących organizmów w tickach. Niski wiek = dużo młodych (populacja rośnie lub szybko się odnawia), wysoki = stabilne, długo żyjące osobniki.',
    see: ['age'],
  },
  'kpi.gen': {
    group: 'Analiza i statystyka',
    title: 'Pokolenia (maksymalne)',
    short: 'Najwyższy numer pokolenia wśród żyjących. Pod spodem: średni numer pokolenia.',
    see: ['generation'],
  },
  'kpi.energy': {
    group: 'Analiza i statystyka',
    title: 'Średnia energia',
    short: 'Jaką część swojej pojemności energii mają średnio organizmy. Niska wartość oznacza głód, wysoka — obfitość.',
    see: ['energy'],
  },
  'kpi.resources': {
    group: 'Analiza i statystyka',
    title: 'Zasoby (ułamek pojemności)',
    short: 'Ile pokarmu jest w świecie w porównaniu z maksimum, jakie mógłby pomieścić. Niskie wartości = wyjedzone pastwisko.',
    more: ['Z drapieżnikami zasoby często stoją znacznie wyżej niż bez nich, bo ofiary są przetrzebione — to kaskada troficzna.'],
    see: ['resources', 'trophic'],
  },
  'kpi.diversity': {
    group: 'Analiza i statystyka',
    title: 'Różnorodność',
    short: 'Średnie odchylenie standardowe cech funkcjonalnych. 0 = wszyscy identyczni.',
    see: ['diversity'],
  },
  'kpi.reprorate': {
    group: 'Analiza i statystyka',
    title: 'Tempo reprodukcji',
    short: 'Ile urodzin na 1000 ticków przypada na jednego żywego organizmu (w promilach). Pokazuje „płodność” populacji.',
    see: ['reproduction'],
  },

  // ——— wykresy ———
  'chart.pop': {
    group: 'Analiza i statystyka',
    title: 'Wykres: populacja i demografia',
    short: 'Zielone pole: liczba żywych organizmów (lewa oś). Zielona i czerwona linia (prawa oś): narodziny i zgony na 1000 ticków.',
    more: ['Szukaj gwałtownych spadków (zdarzenia, głód), oscylacji (cykle drapieżnik–ofiara lub pory roku) i wypłaszczenia (równowaga).'],
    see: ['lotka', 'carrying', 'chart.compaction'],
  },
  'chart.traits': {
    group: 'Analiza i statystyka',
    title: 'Wykres: ewolucja cech',
    short: 'Średnia wartość każdego genu funkcjonalnego w populacji w czasie (skala 0–1). Trend w górę lub w dół to dobór (lub dryf).',
    more: ['Najlepsze miejsce, by zobaczyć ewolucję na własne oczy. Szukaj cech, które systematycznie rosną lub maleją po zmianie parametru. Linie, które „wędrują” bez wyraźnego powodu, bywają dryfem lub hitchhikingiem.'],
    try: 'Zmień koszt ruchu i obserwuj, czy zmieni się „Prędkość maks.”.',
    see: ['selection', 'drift', 'hitchhiking'],
  },
  'chart.energy': {
    group: 'Analiza i statystyka',
    title: 'Wykres: energia i zasoby',
    short: 'Żółta linia: średni zapas energii organizmów. Zielona: ile pokarmu zostało w świecie. Razem pokazują, kto komu „zjada” pastwisko.',
    see: ['resources', 'kpi.energy'],
  },
  'chart.diversity': {
    group: 'Analiza i statystyka',
    title: 'Wykres: różnorodność genetyczna',
    short: 'Fioletowa: rozrzut cech. Niebieska (prawa oś): średnia odległość genomów. Spadek to utrata zmienności (dryf, wymiatanie), wzrost to jej odbudowa.',
    see: ['diversity', 'genomedist', 'sweep'],
  },
  'chart.gens': {
    group: 'Analiza i statystyka',
    title: 'Wykres: pokolenia',
    short: 'Najwyższe i średnie pokolenie w czasie. Nachylenie pokazuje, ile pokoleń przybywa na tysiąc ticków.',
    see: ['generation'],
  },
  'chart.deaths': {
    group: 'Analiza i statystyka',
    title: 'Wykres: przyczyny zgonów',
    short: 'Ile organizmów ginie z jakiej przyczyny na 1000 ticków: głód, drapieżnik, starość, zdrowie, wypadek, katastrofa.',
    more: ['Zmiana proporcji mówi, co właśnie ogranicza populację: np. dominujący głód to znak, że zasoby są limitem.'],
    see: ['death'],
  },
  'chart.predation': {
    group: 'Analiza i statystyka',
    title: 'Wykres: drapieżnictwo',
    short: 'Czerwona: udział drapieżników (dieta > 0,3). Pomarańczowa: ile energii przejęły od ofiar na 1000 ticków.',
    more: ['Spadek udziału do zera oznacza brak drapieżników w tym momencie. W pomiarze v0.3 nie powrócili do końca obserwacji, ale gen diety nadal mutuje; nie dowodzi to nieodwracalności.'],
    see: ['predation', 'absorbing'],
  },
  'chart.clades': {
    group: 'Analiza i statystyka',
    title: 'Wykres: klady',
    short: 'Niebieska: jaką część populacji stanowi największy klad. Fioletowa (prawa oś): ile kladów jeszcze żyje.',
    more: ['Gdy niebieska zbliża się do 1, jedna linia założycieli wyparła pozostałe (wymiatanie). Wpisy o tym trafiają też do dziennika zdarzeń.'],
    see: ['clade', 'sweep'],
  },
  'chart.brain': {
    group: 'Analiza i statystyka',
    title: 'Wykres: złożoność mózgu i wiek',
    short: 'Suma wartości bezwzględnych wag (miara kosztu utrzymania sieci) i średni wiek organizmów.',
    see: ['param.brainCoef', 'brain'],
  },
  'chart.flow': {
    group: 'Analiza i statystyka',
    title: 'Wykres: przepływ energii',
    short: 'Zielona: ile energii wpływa do świata (wzrost zasobów). Czerwona: ile rozprasza się jako ciepło. W równowadze są sobie bliskie.',
    see: ['ledger'],
  },
  'chart.compaction': {
    group: 'Analiza i statystyka',
    title: 'Dlaczego wykresy zmieniają rozdzielczość',
    short: 'Historia statystyk ma stały rozmiar. Gdy się zapełni, sąsiednie punkty są scalane (sumy dodawane, średnie uśredniane), a odstęp próbkowania się podwaja.',
    more: ['Dzięki temu miliony ticków zajmują tyle samo pamięci co tysiące — kosztem szczegółowości najdawniejszej historii.'],
    see: ['tick'],
  },
  'chart.balance': {
    group: 'Analiza i statystyka',
    title: 'Niezgodność bilansu',
    short: 'Różnica między energią, która jest w świecie, a tą, która powinna być wg księgi wpływów i strat. Powinna być praktycznie zerowa.',
    more: ['Jeśli kiedyś zobaczysz tu duże wartości, znaczy to błąd w modelu. Po milionie ticków niezgodność wynosi ok. 10⁻⁵ przy łącznym przepływie energii rzędu 10⁷ — czyli zwykłe zaokrąglenia.'],
    see: ['ledger'],
  },

  // ——— sondy, ablacja, strategie ———
  probes: {
    group: 'Analiza i statystyka',
    title: 'Sondy zachowań',
    short: 'Podajemy sieci pary syntetycznych bodźców (np. pokarm z lewej / z prawej) i sprawdzamy, czy reakcja jest „rozsądna”. Wynik jest porównany z losowymi sieciami.',
    more: [
      'Wynik dodatni oznacza odruch, który wygląda adaptacyjnie (np. skręt w stronę pokarmu). „z” pokazuje, o ile błędów standardowych wynik odbiega od losowych sieci — wartości poniżej 4 traktuj jak szum.',
      'Sondy wykrywają tylko proste odruchy wejście → wyjście. Nie wykryją pamięci, planowania ani zachowań zależnych od historii.',
    ],
    fun: 'Taksja — ruch w stronę lub od bodźca — to jedno z najstarszych zachowań w świecie żywym; potrafią ją już bakterie.',
    see: ['zscore', 'reflex', 'ablation'],
  },
  zscore: {
    group: 'Analiza i statystyka',
    title: 'Wartość z (odchylenie standardowe)',
    short: 'Ile błędów standardowych dzieli wynik od punktu odniesienia. Im większa wartość bezwzględna, tym mniej prawdopodobne, że to przypadek.',
    more: ['|z| ≈ 2 odpowiada p ≈ 0,05, |z| ≈ 3 — p ≈ 0,003. Gdy robisz wiele porównań naraz, część „istotnych” wyników pojawi się przypadkiem — dlatego w teście ablacji za istotne uznajemy dopiero |z| ≥ 3.'],
    see: ['pvalue', 'multiple'],
  },
  ablation: {
    group: 'Analiza i statystyka',
    title: 'Test ablacji (czy zmysł się opłaca?)',
    short: 'W kopii świata odbieramy połowie organizmów jeden zmysł (np. pamięć), a drugiej połowie nie. Porównujemy ich przeżycie i liczbę potomstwa.',
    more: [
      'Zmysł „odbieramy” podmieniając jego wejścia na stałą średnią populacji — znika informacja, ale sieć działa w znanym sobie zakresie (zerowanie przesuwałoby jej punkt pracy i dawało mylące wyniki).',
      'Obie grupy żyją w tym samym świecie, więc nie zaburzamy ekologii (gdyby wszyscy stracili zmysł, np. drapieżnicy przestaliby widzieć ofiary i populacja urosłaby, co nie znaczy, że zmysł był zbędny). Odejmujemy też kontrast zerowy — wynik tej samej procedury bez ablacji — żeby usunąć różnice wynikające z samego podziału na parzyste i nieparzyste id.',
      'Wynik dodatni: zmysł się opłaca. Około zera: organizmy go nie wykorzystują. Ujemny: korzystanie z niego szkodzi. Test nie mówi, co sieć z danym zmysłem robi — tylko że z niego korzysta.',
    ],
    fun: 'To odpowiednik badań uszkodzeń (lesion studies) w neuronauce i „badań ablacyjnych” w uczeniu maszynowym: wyłączasz element i patrzysz, co się psuje.',
    try: 'Poczekaj kilkadziesiąt tysięcy ticków, kliknij „Uruchom test” i zobacz, które zmysły się opłacają. Czucie temperatury powinno wyjść dokładnie 0 — to kontrola metody (to wejście jest stałe).',
    see: ['cohort', 'zscore', 'multiple', 'memory'],
  },
  cohort: {
    group: 'Analiza i statystyka',
    title: 'Kohorta',
    short: 'Grupa organizmów obserwowana razem od wspólnego momentu startu. Tu: połowa ablowana i połowa nienaruszona, śledzone przez ustalone okno ticków.',
    more: ['Dla każdej kohorty liczymy, jaki odsetek przeżył, ile potomstwa urodziło i jaką ma energię. Wynik uwzględnia także organizmy, które zmarły w trakcie okna — inaczej mielibyśmy „błąd przetrwałych”.'],
    see: ['ablation'],
  },
  strategies: {
    group: 'Analiza i statystyka',
    title: 'Profile genetyczne (klastrowanie)',
    short: 'Algorytm k-średnich dzieli organizmy na 4 grupy o podobnych cechach. Etykiety wynikają z tego, czym grupa najbardziej odbiega od średniej populacji.',
    more: ['Słupki pod nazwą to wartości cech grupy (kolejność jak w legendzie pod listą). Pokazuje różne zestawy cech; rzeczywiście wykonywane zachowania grupuje osobno Atlas.', 'Podział jest deterministyczny (zawsze ten sam dla tego samego stanu), ale nazwy to uproszczenie — to nie są gatunki.'],
    fun: 'Algorytm k-średnich zna się od lat 50. i 60. XX wieku; mimo prostoty nadal jest jednym z najczęściej używanych narzędzi grupowania danych.',
    see: ['niche', 'diversity'],
  },

  // ——— statystyka eksperymentów ———
  series: {
    group: 'Analiza i statystyka',
    title: 'Seria seedów',
    short: 'Ta sama konfiguracja uruchomiona wiele razy z różnymi seedami. Pokazuje, co jest regułą, a co — przypadkiem jednego przebiegu.',
    more: ['Pojedynczy przebieg to jedna historia. Ewolucja jest wrażliwa na przypadek, więc wnioski wymagają kilku powtórzeń. Tabela pokazuje wyniki każdego seeda i średnią z 95% przedziałem ufności.'],
    see: ['ci95', 'seed', 'hitchhiking'],
  },
  ci95: {
    group: 'Analiza i statystyka',
    title: '95% przedział ufności',
    short: 'Zakres, w którym — przy powtarzaniu całego eksperymentu — mieściłaby się prawdziwa średnia w ok. 95% przypadków.',
    more: ['Nie oznacza to, że „z prawdopodobieństwem 95% prawdziwa wartość jest w tym przedziale”. Im więcej seedów, tym węższy przedział. Jeśli dwa przedziały się nie zachodzą, różnica jest zwykle istotna; jeśli się zachodzą, trzeba sprawdzić testem.'],
    fun: 'Test t wymyślił William Gosset, pracujący w browarze Guinnessa, który pisał jako „Student” (1908).',
    see: ['welch', 'pvalue'],
  },
  welch: {
    group: 'Analiza i statystyka',
    title: 'Test t Welcha',
    short: 'Sprawdza, czy średnie dwóch grup (A i B) różnią się bardziej, niż wynikałoby z samego przypadku. Nie zakłada równych wariancji.',
    more: ['Zwraca różnicę średnich z przedziałem ufności, statystykę t, stopnie swobody i wartość p. Przy małej liczbie seedów test ma małą moc: brak istotności nie dowodzi braku efektu.'],
    fun: 'Test opisał Bernard Welch w 1947 r. jako wariant testu Studenta.',
    see: ['pvalue', 'ci95', 'ab'],
  },
  pvalue: {
    group: 'Analiza i statystyka',
    title: 'Wartość p',
    short: 'Prawdopodobieństwo uzyskania różnicy co najmniej tak dużej jak zaobserwowana, gdyby w rzeczywistości nie było żadnego efektu.',
    more: ['To nie jest prawdopodobieństwo, że hipoteza jest prawdziwa ani że efekt jest duży. p < 0,05 to konwencja, nie granica prawdy. Warto patrzeć na wielkość efektu i przedział ufności, a nie tylko na p.'],
    see: ['welch', 'multiple'],
  },
  multiple: {
    group: 'Analiza i statystyka',
    title: 'Wielokrotne porównania',
    short: 'Im więcej rzeczy porównujesz, tym większa szansa, że któraś wyjdzie „istotna” z samego przypadku.',
    more: ['Test ablacji sprawdza 7 grup × 2 miary = 14 porównań. Więcej porównań zwiększa ryzyko fałszywego alarmu; próg |z| ≥ 3 jest orientacyjny i nie zastępuje korekty. Dlatego w tabeli ablacji jaśniej świecą dopiero wiersze z |z| ≥ 3.'],
    fun: 'Ten błąd bywa nazywany p-hackingiem, gdy jest (nie)świadomie wykorzystywany: przetestuj wystarczająco wiele hipotez, a któraś „wyjdzie”.',
    see: ['pvalue', 'zscore'],
  },
  ab: {
    group: 'Analiza i statystyka',
    title: 'Test A/B',
    short: 'Dwie wartości jednego parametru, każda uruchomiona na tych samych seedach. Wynik: różnica średnich, przedział ufności i wartość p.',
    more: ['To najprostszy eksperyment przyczynowy: zmieniasz jedną rzecz, resztę zostawiasz tak samo. Wybierz metrykę (np. liczbę drapieżników, różnorodność), parametr i dwie wartości, a aplikacja policzy wszystko w osobnym wątku.'],
    try: 'Parametr „Siła ataku drapieżników”: A = 0, B = 4; metryka „Populacja końcowa”.',
    see: ['paired', 'ci95', 'twin'],
  },
  paramsweep: {
    group: 'Analiza i statystyka',
    title: 'Przeszukiwanie parametrów',
    short: 'Przebiega siatkę wartości jednego lub dwóch parametrów, dla każdej kombinacji uruchamia kilka seedów i pokazuje średnią metryki jako mapę cieplną.',
    more: ['Pozwala znaleźć „progi” — wartości, przy których zachowanie świata się zmienia (np. gdy słaby atak przestaje podtrzymywać drapieżników). Najechanie na komórkę pokazuje przedział ufności i liczbę wymarć.'],
    fun: 'W teorii układów złożonych takie skokowe zmiany zachowania przy płynnej zmianie parametru bywają nazywane przejściem fazowym.',
    see: ['ab', 'series'],
  },
  heatmap: {
    group: 'Analiza i statystyka',
    title: 'Mapa cieplna',
    short: 'Każda komórka to jedna kombinacja parametrów; jaśniejsza = wyższa średnia metryki. Krzyżyk ✝ oznacza przebiegi, w których populacja wymarła.',
    see: ['paramsweep'],
  },
  compare: {
    group: 'Analiza i statystyka',
    title: 'Porównanie światów',
    short: 'Wykres jednej metryki dla wszystkich otwartych światów naraz, z tabelą bieżących wartości. Dla dwóch światów pokazuje też różnicę.',
    more: ['Najlepiej używać z bliźniakami: wspólny początek, jedna zmiana. Różnica między nimi jest dokładnie efektem tej zmiany.'],
    see: ['twin', 'fork'],
  },
  log: {
    group: 'Analiza i statystyka',
    title: 'Dziennik zdarzeń',
    short: 'Lista ważnych wydarzeń: zmiany parametrów, wywołane zdarzenia, gwałtowne spadki i wzrosty populacji, wymiatanie kladów, wymarcie.',
    see: ['commandlog', 'sweep'],
  },
  exportdata: {
    group: 'Analiza i statystyka',
    title: 'Eksport danych',
    short: 'Statystyki do CSV, cały eksperyment (konfiguracja, historia zmian, zdarzenia, statystyki) do JSON, genealogię do JSON i bieżącą populację do CSV.',
    more: ['Dane możesz otworzyć w arkuszu kalkulacyjnym lub w Pythonie i narysować własne wykresy.'],
    see: ['save'],
  },

  // ——— genealogia ———
  'tree.legend': {
    group: 'Analiza i statystyka',
    title: 'Jak czytać drzewo rodowe',
    short: 'Kropki to organizmy, linie to relacje rodzic–dziecko. Turkusowa = żyje, szara = zmarły. Rozmiar kropki rośnie z liczbą potomstwa. Klik w kropkę zaznacza organizm.',
    more: ['Po lewej przodkowie aż do założyciela (długie ciągi są skracone znakiem ⋯), po prawej potomkowie wybranego organizmu. W rozmnażaniu płciowym drzewo biegnie wzdłuż pierwszego rodzica.'],
    see: ['lineage', 'clade'],
  },
  'tree.sparks': {
    group: 'Analiza i statystyka',
    title: 'Cechy wzdłuż linii przodków',
    short: 'Małe wykresy pokazują, jak zmieniała się każda cecha od założyciela do wybranego organizmu. Zielone liczby = wzrost, czerwone = spadek.',
    more: ['To ewolucja „pod mikroskopem”: widzisz, które cechy zmieniały się stopniowo, a które skokowo. Po scaleniu łańcuchów na wykresie zostają tylko węzły ważne dla drzewa.'],
    see: ['lineage', 'selection'],
  },
  'tree.compare': {
    group: 'Analiza i statystyka',
    title: 'Porównanie z przodkiem',
    short: 'Suwak wybiera przodka. Poniżej: różnica w cechach, liczba pokoleń, zmiany genów i dwa paski genomu z podświetlonymi różnicami.',
    more: ['Lista „największe zmiany wag mózgu” pokazuje połączenia, które zmieniły się najmocniej — np. „jedzenie ← → skręt”.'],
    see: ['genome', 'lineage'],
  },
};
